/** Canonical Bedrock invoice keys and date helpers (mirrors shared/lib/bedrock_normalize.py). */

const KEY_ALIASES = {
  'iva 21%': 'iva_21',
  'iva 10.5%': 'iva_10_5',
  'iva 10,5%': 'iva_10_5',
  'codigo factura': 'codigo_factura',
  'condiciones de venta': 'condiciones_de_venta',
  'orden de compra': 'orden_de_compra',
  'fecha vencimiento cae': 'cae_vencimiento',
  fecha_vencimiento_cae: 'cae_vencimiento',
  fecha_vencimiento_CAE: 'cae_vencimiento',
};

const SPANISH_MONTHS = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

export function normalizeFechaEmision(value) {
  if (value == null || value === '') return '';
  const text = String(value).trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);

  const numeric = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (numeric) {
    let [, d, m, y] = numeric;
    let year = parseInt(y, 10);
    if (year < 100) year += 2000;
    return `${String(year).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  const word = text.match(/^(\d{1,2})\s+de\s+([A-Za-záéíóúñ]+)\s+de\s+(\d{4})$/i);
  if (word) {
    const day = parseInt(word[1], 10);
    const monthName = word[2]
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const year = parseInt(word[3], 10);
    const month = SPANISH_MONTHS[monthName];
    if (month) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  return text;
}

export function normalizeBedrockKeys(data) {
  if (!data || typeof data !== 'object') return {};
  const out = { ...data };
  Object.entries(KEY_ALIASES).forEach(([alias, canonical]) => {
    if (!(alias in out)) return;
    const aliasVal = out[alias];
    delete out[alias];
    if ((out[canonical] == null || out[canonical] === '') && aliasVal != null && aliasVal !== '') {
      out[canonical] = aliasVal;
    }
  });
  return out;
}

export function canonicalizeBedrockInvoice(data) {
  const out = normalizeBedrockKeys(data);
  ['fecha_emision', 'fecha_vencimiento', 'cae_vencimiento'].forEach((field) => {
    if (out[field]) out[field] = normalizeFechaEmision(out[field]);
  });
  return out;
}

export function getBedrockField(data, key, defaultValue = null) {
  const normalized = normalizeBedrockKeys(data || {});
  const val = normalized[key];
  if (val != null && val !== '') return val;
  const alias = Object.entries(KEY_ALIASES).find(([, canon]) => canon === key)?.[0];
  if (alias && data?.[alias] != null && data[alias] !== '') return data[alias];
  return defaultValue;
}

export function getBedrockConfidence(data, key) {
  const fc = data?.field_confidence;
  if (!fc || typeof fc !== 'object') return null;
  const score = fc[key];
  if (score == null || score === '') return null;
  const n = Number(score);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : null;
}

/** Full Bedrock JSON: prefer GraphQL metadata over mapped bedrock_model_output. */
export function resolveBedrockMetadata(source) {
  if (!source) return {};

  const bedrock =
    source.bedrock_model_output
    ?? source.record?.bedrock_model_output
    ?? (source.metadata != null && typeof source.metadata === 'object' ? source : null);

  if (!bedrock || typeof bedrock !== 'object') return {};

  if (bedrock.metadata != null && typeof bedrock.metadata === 'object') {
    return bedrock.metadata;
  }

  return bedrock;
}
